/**
 * Global upload tray state.
 *
 * Single source of truth for every in-flight upload across the app. The
 * `<v-upload-tray>` mounted once in `App.vue` reads from here; both the
 * Documents page and `VDocumentUploader` push items via `services/upload.js`.
 *
 * Item shape mirrors `VUploadTray`'s contract:
 *   { id, name, sizeLabel, status: 'uploading' | 'done' | 'error', error? }
 *
 * Dismiss behavior matches the Documents page (`Documents.vue` pre-refactor):
 * once the queue settles (no `uploading` items left), hold for ~3s so the
 * green check is unmistakable, then clear.
 */

const SETTLE_DISMISS_MS = 3000;

const clearTimer = state => {
    if (state.dismissTimerId) {
        clearTimeout(state.dismissTimerId);
        state.dismissTimerId = null;
    }
};

export default {
    namespaced: true,
    state: {
        items: [],
        dismissTimerId: null,
    },
    mutations: {
        PUSH_ITEMS(state, items) {
            state.items = [...state.items, ...items];
        },
        UPDATE_ITEM(state, { id, patch }) {
            const idx = state.items.findIndex(i => i.id === id);
            if (idx >= 0) state.items.splice(idx, 1, { ...state.items[idx], ...patch });
        },
        CLEAR(state) {
            state.items = [];
        },
        SET_TIMER(state, timerId) {
            state.dismissTimerId = timerId;
        },
    },
    actions: {
        /**
         * Add files to the tray as `uploading` items and return their ids in input order.
         * Cancels any pending dismiss timer — a new batch resets the settle clock.
         */
        enqueue({ commit, state }, files) {
            clearTimer(state);
            commit('SET_TIMER', null);
            const stamp = Date.now();
            const items = files.map((file, idx) => ({
                id: `up-${stamp}-${idx}-${Math.random().toString(36).slice(2, 7)}`,
                name: file.name,
                sizeLabel: formatBytes(file.size),
                status: 'uploading',
            }));
            commit('PUSH_ITEMS', items);
            return items.map(i => i.id);
        },
        markDone({ commit }, id) {
            commit('UPDATE_ITEM', { id, patch: { status: 'done' } });
        },
        markError({ commit }, { id, error }) {
            commit('UPDATE_ITEM', { id, patch: { status: 'error', error } });
        },
        /**
         * Schedule a dismiss `delay` ms after the queue settles. Safe to call
         * repeatedly — the latest call wins. Does nothing while any item is
         * still `uploading`.
         */
        scheduleDismiss({ commit, state }, { delay = SETTLE_DISMISS_MS } = {}) {
            if (state.items.some(i => i.status === 'uploading')) return;
            clearTimer(state);
            const timerId = setTimeout(() => {
                commit('CLEAR');
                commit('SET_TIMER', null);
            }, delay);
            commit('SET_TIMER', timerId);
        },
        dismiss({ commit, state }) {
            clearTimer(state);
            commit('SET_TIMER', null);
            commit('CLEAR');
        },
    },
};

// Local copy of `formatBytes` from `services/documents` so this module stays
// dependency-free (importing services/documents pulls in axios + store).
function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes >= 1024 * 1024) {
        const mb = bytes / (1024 * 1024);
        return mb >= 10 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
    }
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
}
